import UnsavedChangesModal from 'components/util/UnsavedChangesModal';
import { Form, FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { NewOrganization } from '../../../@types/organization';
import { getClaimsByTemplateRole, USER_ACCESS_TEMPLATE_ROLES } from 'utils/userAccessTemplates';
import * as Yup from 'yup';
import { addOrganization, getChildTenants } from 'redux/slices/organization';
import { getPermissions } from 'redux/slices/permissions';
import useCustomDispatch from 'redux/dispatch';
import { RootState, useSelector } from 'redux/store';
import PhoneNumberInputField from 'components/util/PhoneNumberInputField';
import { useRegisterUnsavedFormChanges } from 'components/form/useRegisterUnsavedFormChanges';
import getFormikValidateFunction from 'utils/getFormikValidateFunction';
import { getPhoneNumberYupTest } from 'utils/validation/isPhoneNumberValid';
import { getVatYupTest } from 'utils/validation/isVatValid';
import { useTranslator } from 'translation/useTranslator';
import { Button, TextField } from '@nps.today/component-library';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import OrganizationUserPermissions from 'pages/organizations/components/OrganizationUserPermissions';
import useAuth from 'hooks/useAuth';
import { isAccessible, rules } from 'access';

const componentDataTestId = 'add-organization-dialog';

const NEW_ORGANIZATION_CAPABILITIES = { isAiFeedbackEnabled: false };

const OrganizationStepFields: (keyof NewOrganization)[] = [
  'organizationName',
  'displayName',
  'organizationPhone',
  'invoiceEmail',
  'website',
  'vat'
];

type AddOrganizationDialogProps = {
  display: boolean;
  close: () => void;
  isLinkedOrg?: boolean;
};
const AddOrganizationDialog = ({ display, close, isLinkedOrg }: AddOrganizationDialogProps) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const auth = useAuth();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const { loading, organizationProfile } = useSelector((state: RootState) => state.organization);
  const { assignableRoles, isLoading: isPermissionsLoading } = useSelector(
    (state: RootState) => state.permissions
  );
  const canAssignClaims = isAccessible(
    rules.claimAssignment.any.claims ?? [],
    auth.user?.claims ?? []
  );

  useEffect(() => {
    customDispatch({ action: getPermissions, disableSuccessMessage: true });
  }, [customDispatch]);

  const UpdateOrganizationSchema = Yup.object().shape({
    contactEmail: Yup.string()
      .email(translator.emailInvalid())
      .required(translator.emailRequired()),
    confirmContactEmail: Yup.string()
      .required(translator.emailRequired())
      .oneOf([Yup.ref('contactEmail')], translator.contactEmailMatch()),
    organizationName: Yup.string().required(translator.organizationNameIsRequired()),
    displayName: Yup.string().required(translator.displayNameIsRequired()),
    organizationPhone: Yup.string().test(getPhoneNumberYupTest()),
    invoiceEmail: Yup.string().email(),
    vat: Yup.string().required(translator.vatIsRequired()).test(getVatYupTest()),
    role: Yup.string().required(translator.fieldRequiredError(translator.userAccessTemplate()))
  });

  const formik = useFormik<NewOrganization>({
    enableReinitialize: true,
    initialValues: {
      contactEmail: '',
      confirmContactEmail: '',
      organizationName: '',
      displayName: '',
      organizationPhone: (isLinkedOrg && organizationProfile.organizationPhone) || '',
      invoiceEmail: (isLinkedOrg && organizationProfile.invoiceEmail) || '',
      website: (isLinkedOrg && organizationProfile.website) || '',
      vat: '',
      role: '',
      claims: []
    },
    validate: getFormikValidateFunction(UpdateOrganizationSchema, {
      initialorganizationPhone: (isLinkedOrg && organizationProfile.organizationPhone) || ''
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      customDispatch({
        action: addOrganization,
        actionParameters: { ...values },
        onSuccess: async () => {
          onClose();

          customDispatch({ action: getChildTenants, disableSuccessMessage: true });
        },
        onFinally: () => {
          setSubmitting(false);
        }
      });
    }
  });

  const {
    submitForm,
    resetForm,
    validateForm,
    errors,
    touched,
    dirty,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    isValid
  } = formik;

  useRegisterUnsavedFormChanges(dirty);

  useEffect(() => {
    if (display) {
      validateForm();
    }
  }, [display, validateForm]);

  const onClose = () => {
    close();
    resetForm();
    setShowPermissions(false);
    setActiveStep(0);
  };

  const handleCloseRequest = () => {
    if (dirty) {
      setIsUnsavedChangesModalOpen(true);
      return;
    }
    onClose();
  };

  const handleAccessTemplateChange = (templateRole: string) => {
    formik.setValues({
      ...formik.values,
      role: templateRole,
      claims: getClaimsByTemplateRole(templateRole, NEW_ORGANIZATION_CAPABILITIES)
    });
  };

  const isOrganizationStepValid = OrganizationStepFields.every((field) => !errors[field]);

  const handleNext = () => setActiveStep((step) => step + 1);
  const handleBack = () => setActiveStep((step) => step - 1);

  const dialogTitle = isLinkedOrg
    ? translator.addNewLinkedOrganization()
    : translator.addNewOrganization();

  return (
    <>
      <Dialog open={display} onClose={handleCloseRequest} maxWidth="lg">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Box sx={{ width: '70vw', maxWidth: '40rem', pt: 1 }}>
                <Stepper activeStep={activeStep} orientation="vertical">
                  <Step>
                    <StepLabel>{translator.organizationDetailsSectionTitle()}</StepLabel>
                    <StepContent>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            dataTestId={`${componentDataTestId}-organization-name`}
                            fullWidth
                            label={translator.organizationName()}
                            {...getFieldProps('organizationName')}
                            required
                            error={Boolean(touched.organizationName && errors.organizationName)}
                            helperText={touched.organizationName && errors.organizationName}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            dataTestId={`${componentDataTestId}-display-name`}
                            fullWidth
                            label={translator.displayName()}
                            {...getFieldProps('displayName')}
                            required
                            error={Boolean(touched.displayName && errors.displayName)}
                            helperText={touched.displayName && errors.displayName}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <PhoneNumberInputField
                            dataTestId={`${componentDataTestId}-organization-phone`}
                            fieldName="organizationPhone"
                            label={translator.organizationPhone()}
                            formik={formik}
                            initialValue={''}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            dataTestId={`${componentDataTestId}-invoice-email`}
                            fullWidth
                            label={translator.invoceEmail()}
                            {...getFieldProps('invoiceEmail')}
                            error={Boolean(touched.invoiceEmail && errors.invoiceEmail)}
                            helperText={touched.invoiceEmail && errors.invoiceEmail}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            dataTestId={`${componentDataTestId}-website`}
                            fullWidth
                            label={translator.website()}
                            {...getFieldProps('website')}
                            error={Boolean(touched.website && errors.website)}
                            helperText={touched.website && errors.website}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            dataTestId={`${componentDataTestId}-license-vat`}
                            required
                            fullWidth
                            label={translator.vatNumber()}
                            {...getFieldProps('vat')}
                            error={Boolean(touched.vat && errors.vat)}
                            helperText={touched.vat && errors.vat}
                            disabled={isLinkedOrg}
                          />
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                        <Button
                          dataTestId={`${componentDataTestId}-continue`}
                          variant="contained"
                          onClick={handleNext}
                          disabled={!isOrganizationStepValid}
                        >
                          {translator.continue()}
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>
                  <Step>
                    <StepLabel>{translator.initialUserSectionTitle()}</StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {translator.initialUserSectionDescription()}
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            dataTestId={`${componentDataTestId}-contact-email`}
                            fullWidth
                            label={translator.contactEmail()}
                            {...getFieldProps('contactEmail')}
                            required
                            error={Boolean(touched.contactEmail && errors.contactEmail)}
                            helperText={touched.contactEmail && errors.contactEmail}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            dataTestId={`${componentDataTestId}-confirm-contact-email`}
                            fullWidth
                            label={translator.confirmContactEmail()}
                            {...getFieldProps('confirmContactEmail')}
                            required
                            onPaste={(event) => event.preventDefault()}
                            onDrop={(event) => event.preventDefault()}
                            error={Boolean(
                              touched.confirmContactEmail && errors.confirmContactEmail
                            )}
                            helperText={touched.confirmContactEmail && errors.confirmContactEmail}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            dataTestId={`${componentDataTestId}-user-access-template`}
                            disabled={isPermissionsLoading}
                            select
                            fullWidth
                            required
                            label={translator.userAccessTemplate()}
                            value={formik.values.role}
                            onChange={(event: any) =>
                              handleAccessTemplateChange(event.target.value)
                            }
                            error={Boolean(touched.role && errors.role)}
                            helperText={touched.role && errors.role}
                          >
                            {assignableRoles.includes('CompanyAdmin') && (
                              <MenuItem value={USER_ACCESS_TEMPLATE_ROLES.admin}>
                                {translator.companyAdmin()}
                              </MenuItem>
                            )}
                            {assignableRoles.includes('CompanyUser') && (
                              <MenuItem value={USER_ACCESS_TEMPLATE_ROLES.campaignManager}>
                                {translator.campaignManager()}
                              </MenuItem>
                            )}
                          </TextField>
                        </Grid>
                        <Grid size={12}>
                          <Button
                            dataTestId={`${componentDataTestId}-toggle-permissions`}
                            variant={'text'}
                            onClick={() => setShowPermissions((current) => !current)}
                          >
                            {showPermissions
                              ? translator.hidePermissions()
                              : translator.viewPermissions()}
                          </Button>
                        </Grid>
                        {showPermissions && (
                          <Grid size={12}>
                            <OrganizationUserPermissions
                              selectedClaims={formik.values.claims}
                              isEditable
                              canAssignClaims={canAssignClaims}
                              capabilities={NEW_ORGANIZATION_CAPABILITIES}
                              onClaimsChange={(updatedClaims) =>
                                formik.setFieldValue('claims', updatedClaims)
                              }
                            />
                          </Grid>
                        )}
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                        <Button
                          dataTestId={`${componentDataTestId}-save`}
                          variant="contained"
                          disabled={!isValid || loading.addOrganization}
                          loading={isSubmitting || loading.addOrganization}
                          onClick={() => submitForm()}
                        >
                          {translator.save()}
                        </Button>
                        <Button
                          dataTestId={`${componentDataTestId}-back`}
                          variant={'text'}
                          onClick={handleBack}
                        >
                          {translator.back()}
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>
                </Stepper>
              </Box>
            </Form>
          </FormikProvider>
        </DialogContent>
        <DialogActions>
          <Button
            dataTestId={`${componentDataTestId}-cancel`}
            variant={'text'}
            onClick={handleCloseRequest}
          >
            {translator.cancel()}
          </Button>
        </DialogActions>
      </Dialog>
      <UnsavedChangesModal
        isOpen={isUnsavedChangesModalOpen}
        onCancel={() => {
          setIsUnsavedChangesModalOpen(false);
        }}
        onDisregard={() => {
          resetForm();
          setIsUnsavedChangesModalOpen(false);
          onClose();
        }}
        onSave={() => {
          setIsUnsavedChangesModalOpen(false);
          submitForm();
        }}
      />
    </>
  );
};

export default AddOrganizationDialog;
